import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import Filter from '../../components/common/Filter/Filter';
import Sort from '../../components/common/Sort/Sort';
import ExportLabel from '../../components/common/ExportLabel/Export';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import SimpleSearchBox from '../../components/common/Search/SimpleSearch';
import ArchiveSelectedItem from '../../components/common/ArchiveSelectedItemLabel/ArchiveSelectedItems';
import { getFreeTrialUsers } from '../../services/FreeTrial/FreeTrail';
import { useCheckboxClick } from '../../checkboxclick';
import useFilterStore from '../../store/useFilterStore';
import useRefreshStore from '../../store/useRefreshStore';

const pageKey = "free_trial_users";

const FreeTrialUsers = () => {
  const navigate = useNavigate();

  const { setFilters, getFilters } = useFilterStore();
  const savedFilters = getFilters(pageKey);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const [searchParams,         setSearchParams]         = useState(savedFilters?.searchParams         || { term: "" });
  const [selectedFilterOption, setSelectedFilterOption] = useState(savedFilters?.selectedFilterOption || "Unarchived");
  const [selectedSortOption,   setSelectedSortOption]   = useState(savedFilters?.selectedSortOption   || "Newest");

  const { refreshKeys } = useRefreshStore();
  const refreshKey = refreshKeys["Free Trial Users"] || 0;

  const {
    isParentSelected,
    selectedItems,
    handleParentCheckboxChange,
    handleChildCheckboxChange,
    resetCheckboxState,
  } = useCheckboxClick(items);

  const sortOptions   = ['Newest', 'Oldest', 'A-Z', 'Z-A'];
  const filterOptions = ['Default', 'Archived', 'Unarchived'];

  // Fetch when filters change
  useEffect(() => {
    fetchFreeTrialUsers();
    setFilters(pageKey, { searchParams, selectedFilterOption, selectedSortOption });
  }, [searchParams, selectedSortOption, selectedFilterOption, refreshKey]);



  const mapSortOptionToQuery = (option) => {
    switch (option) {
      case 'A-Z':    return 'a_to_z';
      case 'Z-A':    return 'z_to_a';
      case 'Newest': return 'newest';
      case 'Oldest': return 'oldest';
      default:       return 'newest';
    }
  };

  const fetchFreeTrialUsers = async () => {
    setLoading(true);
    try {
      const params = {
        search: searchParams.term,
        filter:
          selectedFilterOption === 'Default'
            ? undefined
            : selectedFilterOption.toLowerCase(),
        sort: mapSortOptionToQuery(selectedSortOption),
      };

      const data = await getFreeTrialUsers(params);
      setItems(data);
      resetCheckboxState();

    } catch (error) {
      console.error("Error fetching free trial users:", error.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatTrialPeriod = (start, end) => {
    if (!start || !end) return '—';
    return `${formatDate(start)} To ${formatDate(end)}`;
  };

  const handleSearchSelect  = (term)   => setSearchParams((prev) => ({ ...prev, term }));
  const handleSortOptionSelect  = (option) => setSelectedSortOption(option);
  const handleFilterSelect      = (option) => setSelectedFilterOption(option);

  return (
    <div className="whole-container">

      <div className="top-heading">
        <PageHeader pageName="Free Trial /" nestedpageName="Free Trial Users" linkTo="" />
        <div className="export-container">
          <ArchiveSelectedItem 
            selectedItems={selectedItems} 
            items={items}
            onArchiveSelected={() => {}}
          />
          {/* <ExportLabel /> */}
        </div>
      </div>

      <div className="pagename-container">
        <div className="page-name">
          <h1 className="common-text fts-22 ftw-6 blue">Free Trial Users</h1>
        </div>
        <div className="pagename-right-container">
          <SimpleSearchBox
            placeholder="Search Free Trial Users"
            onSearchSelect={handleSearchSelect}
            defaultValue={searchParams?.term}
          />
          <Filter
            options={filterOptions}
            onFilterSelect={handleFilterSelect}
            defaultValue={selectedFilterOption}
          />
          <Sort
            options={sortOptions}
            onOptionSelect={handleSortOptionSelect}
            defaultValue={selectedSortOption}
          />
        </div>
      </div>

      <div className="table-container">
        <table className="common-table">
          <thead className="common-table-head">
            <tr className="common-table-thead-tr">
              <th className="d-flex ai-center">
                <input
                  className="table-selectbox table-child-selectbox"
                  type="checkbox"
                  checked={isParentSelected}
                  onChange={handleParentCheckboxChange}
                />
                COMPANY NAME
              </th>
              <th>EMAIL</th>
              <th>USER ACCOUNTS</th>
              <th>TRIAL PERIOD</th>
              <th>LOCATION</th>
            </tr>
          </thead>

          <tbody className="common-table-tbody">
            {loading ? (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', padding: '20px' }}>
                  Loading...
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', padding: '20px' }}>
                  No free trial users found.
                </td>
              </tr>
            ) : (
              items.map((item) => (
                <tr key={item.id}>
                  <td className="d-flex ai-center">
                    <input
                      className="table-selectbox table-child-selectbox"
                      type="checkbox"
                      checked={(selectedItems ?? []).includes(item.id)}
                      onChange={() => handleChildCheckboxChange(item.id)}
                    />
                    {item.company_name || '—'}
                  </td>
                  <td>{item.email || '—'}</td>
                  <td>{item.user_accounts ?? '—'}</td>
                  <td>{formatTrialPeriod(item.trial_start, item.trial_end)}</td>
                  <td>{item.location || '—'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

    </div>
  );
};

export default FreeTrialUsers;